import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { subscriptionApi } from '../services/api';
import { getApiErrorMessage } from '../utils/examStart';
import { useAuth } from './useAuth';
import { Button } from '../components/ui';
import './useUpgrade.css';

export interface SubscriptionSummary {
  entitlement: { aiPower: boolean; source: 'admin' | 'manual' | 'subscription' | 'trial' | null; until: string | null };
  plan: { id: string; name: string; price: number; currency: string; trialDays: number };
  subscription: { status: 'trialing' | 'active' | 'past_due' | 'canceling' | 'expired'; trial_started_at?: string; trial_ends_at?: string; current_period_end?: string; next_billing_at?: string; card_summary?: { company?: string; number?: string } } | null;
  trialAvailable: boolean;
  billingReady: boolean;
}

interface UpgradeContextValue {
  /** AI Power User면 true. 아니면 구독 안내 팝업을 띄우고 false */
  requireAiPower: (featureName: string) => boolean;
  /** 권한 확인 없이 안내 팝업만 띄웁니다 (서버가 잠김이라고 알려 준 본 시험 등). kind='exam'이면 시험 문구 */
  promptUpgrade: (name: string, kind?: 'feature' | 'exam') => void;
  /** 요금제·내 구독 화면으로 이동 */
  openPlans: () => void;
}

const UpgradeContext = createContext<UpgradeContextValue | null>(null);

export const useUpgrade = (): UpgradeContextValue => {
  const value = useContext(UpgradeContext);
  if (!value) throw new Error('useUpgrade must be used within UpgradeProvider');
  return value;
};

export const formatKrw = (amount: number) => `${amount.toLocaleString('ko-KR')}원`;
export const formatDay = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('ko-KR', { timeZone: 'Asia/Seoul', year: 'numeric', month: 'long', day: 'numeric' }) : '');

/**
 * 유료(AI Power User) 기능 버튼은 모두에게 보이고, 권한이 없으면 이 팝업이 뜹니다.
 *   체험 이력 없음 → "1달 무료 체험 해 보시겠습니까?" (카드 없이 바로 시작)
 *   체험 이력 있음 → "구독하시겠습니까?" → 요금제(플랜 선택) → 결제
 */
export const UpgradeProvider: React.FC<{ children: React.ReactNode; onOpenPlans: () => void }> = ({ children, onOpenPlans }) => {
  const { user, refreshUser } = useAuth();
  const [feature, setFeature] = useState<string | null>(null);
  const [kind, setKind] = useState<'feature' | 'exam'>('feature');
  const [summary, setSummary] = useState<SubscriptionSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [started, setStarted] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const entitled = Boolean(user?.is_admin || user?.ai_power?.aiPower);

  const promptUpgrade = useCallback((name: string, nextKind: 'feature' | 'exam' = 'feature') => {
    setKind(nextKind);
    setFeature(name);
    setStarted(null);
    setError(null);
  }, []);

  const requireAiPower = useCallback((featureName: string) => {
    if (entitled) return true;
    promptUpgrade(featureName, 'feature');
    return false;
  }, [entitled, promptUpgrade]);

  useEffect(() => {
    if (!feature) return;
    setLoading(true);
    subscriptionApi.me()
      .then((response) => setSummary((response as { data?: SubscriptionSummary }).data || null))
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, [feature]);

  useEffect(() => {
    if (!feature) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setFeature(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [feature]);

  const close = () => setFeature(null);

  const openPlans = useCallback(() => {
    setFeature(null);
    onOpenPlans();
  }, [onOpenPlans]);

  const startTrial = async () => {
    setStarting(true);
    setError(null);
    try {
      const response = await subscriptionApi.startTrial() as { data?: SubscriptionSummary };
      setStarted(response.data?.subscription?.trial_ends_at || null);
      await refreshUser();
    } catch (startError) {
      setError(getApiErrorMessage(startError) || '무료 체험을 시작하지 못했습니다.');
    } finally {
      setStarting(false);
    }
  };

  const plan = summary?.plan;
  const trial = summary?.trialAvailable ?? true;

  return (
    <UpgradeContext.Provider value={{ requireAiPower, promptUpgrade, openPlans }}>
      {children}
      {feature && (
        <div className="upgrade__overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) close(); }}>
          <div className="upgrade__dialog" role="dialog" aria-modal="true" aria-labelledby="upgrade-title">
            <p className="upgrade__eyebrow">AI Power User</p>
            {started !== null ? (
              <>
                <h2 id="upgrade-title">무료 체험이 시작됐습니다</h2>
                <p className="upgrade__lead">
                  {started ? `${formatDay(started)}까지` : '30일 동안'} AI Power User 기능을 모두 쓸 수 있습니다. 체험이 끝나도 자동으로 결제되지 않습니다.
                </p>
                <div className="upgrade__actions">
                  <Button autoFocus variant="primary" onClick={close}>{kind === 'exam' ? '시험 선택으로 돌아가기' : `${feature} 사용하기`}</Button>
                </div>
              </>
            ) : (
              <>
                <h2 id="upgrade-title">{kind === 'exam' ? `‘${feature}’ 본 시험을 보려면 구독이 필요합니다` : `‘${feature}’ 기능을 활용하려면 구독이 필요합니다`}</h2>
                {loading ? (
                  <p className="upgrade__lead">구독 정보를 확인하고 있습니다…</p>
                ) : trial ? (
                  <>
                    <p className="upgrade__lead">1달 무료 체험을 해 보시겠습니까?</p>
                    <ul className="upgrade__points">
                      <li>카드 등록 없이 바로 시작, {plan?.trialDays ?? 30}일 동안 본 시험 전체, 오답 모의고사, AI 기능 전부</li>
                      <li>체험이 끝나도 자동 결제되지 않습니다. 계속 쓰려면 직접 구독하세요.</li>
                      <li>체험 후 구독료는 월 {formatKrw(plan?.price ?? 10000)}입니다.</li>
                    </ul>
                  </>
                ) : (
                  <>
                    <p className="upgrade__lead">무료 체험 기간이 끝났습니다. 구독하시겠습니까?</p>
                    <ul className="upgrade__points">
                      <li>{plan?.name ?? 'AI Power User'} 월 {formatKrw(plan?.price ?? 10000)}, 언제든 해지</li>
                    </ul>
                  </>
                )}
                {error && <p className="upgrade__error" role="alert">{error}</p>}
                <div className="upgrade__actions">
                  <Button variant="tertiary" onClick={close}>나중에</Button>
                  {trial ? (
                    <Button autoFocus variant="primary" onClick={startTrial} loading={starting} disabled={loading}>30일 무료 체험 시작</Button>
                  ) : (
                    <Button autoFocus variant="primary" onClick={openPlans} disabled={loading}>플랜 선택</Button>
                  )}
                </div>
                {trial && <button type="button" className="upgrade__link" onClick={openPlans}>요금제 자세히 보기</button>}
              </>
            )}
          </div>
        </div>
      )}
    </UpgradeContext.Provider>
  );
};
